/**
 * Country access (the casino country filter), through owner-hub.
 *
 * The setting is a list of countries and a mode: `block` (everyone except the listed countries can use the casino) or
 * `allow` (only the listed countries can). Owner-hub stores both in the casino's ACL tenant (`content.country_list` /
 * `content.filter_mode`) and keeps no copy. Contract: Thoth `automation-micros-wl_owner_hub/country_filter.md`. The
 * card edits one allow list and always saves a `block` filter of the countries switched off; `allow` still parses,
 * since a filter stored before that can hold one.
 *
 *   GET  {userApiUrl}/casino-instance/country-filter?casino_instance_id=<uuid>
 *        → { country_list: ["RO", "MX"], filter_mode: "block" }   (no filter: { country_list: [], filter_mode: null })
 *   POST {userApiUrl}/casino-instance/country-filter
 *        { casino_instance_id, country_list, filter_mode } → the stored setting, same shape as the GET
 *
 * Codes are ISO 3166-1 alpha-2. An empty `country_list` removes the filter (`filter_mode` is still required, and
 * ignored). Refusals are HTTP 500 `{ error: { code, message } }` like the other owner-hub service errors, so they never
 * trip the global auth teardown; see {@link CountryFilterError}.
 *
 * The global axios response cache ignores this route (`utils/install-axios-cache.ts`): it keys POSTs too, so a save
 * repeated within its window would be answered from the cache and never reach the service.
 */
import axios from 'axios';
import { isNil } from 'lodash-es';

import { store } from '@/store';
import { getErrorCode } from '@/utils/errors';

export type CountryFilterMode = 'allow' | 'block';

export type CountryFilter = {
  /** Alpha-2 codes, upper case, no duplicates. Empty = no filter. */
  countries: string[];
  /** `null` exactly when `countries` is empty. */
  mode: CountryFilterMode | null;
};

/**
 * The service refused or failed. `code` is the owner-hub error code (`CASINO_INSTANCE_NOT_DEPLOYED`,
 * `CASINO_INSTANCE_NOT_FOUND`, `TENANT_NOT_FOUND`, `ACL_TENANT_REQUEST_FAILED`, `CLUSTER_NOT_FOUND`, …), `unexpected`
 * for an answer this module could not read, or '' when the body carried none. `status` is the HTTP status (0 = no
 * answer).
 */
export class CountryFilterError extends Error {
  readonly code: string;
  readonly status: number;
  constructor(code: string, status: number) {
    super(code || `HTTP ${status}`);
    this.name = 'CountryFilterError';
    this.code = code;
    this.status = status;
  }
}

/** The instance has no destination cluster yet, so it has no tenant to hold the setting. */
export const NOT_DEPLOYED = 'CASINO_INSTANCE_NOT_DEPLOYED';

const route = (): string => {
  const base = (window as unknown as { config?: { userApiUrl?: string } }).config?.userApiUrl;
  if (!base) throw new Error('window.config.userApiUrl is not configured');
  return `${base.replace(/\/+$/, '')}/casino-instance/country-filter`;
};

const authHeaders = (): Record<string, string> => {
  const token = store.getState().authentication.access_token;
  if (isNil(token)) throw new CountryFilterError('no_token', 0);
  return { Authorization: token };
};

const ALPHA2 = /^[A-Z]{2}$/;

/** The answer as the card's model: unknown codes and modes dropped, an empty list always with a `null` mode. */
export const parseCountryFilter = (data: unknown): CountryFilter => {
  if (typeof data !== 'object' || data === null) throw new CountryFilterError('unexpected', 200);
  const record = data as Record<string, unknown>;
  // The service always answers a list (empty when there is no filter). Anything else is a failed answer, never "no
  // filter": shown as empty, the owner's next save would replace whatever is really stored.
  if (!Array.isArray(record.country_list)) throw new CountryFilterError('unexpected', 200);
  const countries = [
    ...new Set(
      record.country_list.filter((v): v is string => typeof v === 'string').map((v) => v.trim().toUpperCase()),
    ),
  ].filter((v) => ALPHA2.test(v));
  const raw = record.filter_mode;
  const mode: CountryFilterMode | null = raw === 'allow' || raw === 'block' ? raw : null;
  // A list with no readable mode cannot be shown truthfully as either one; refuse it rather than guess.
  if (countries.length > 0 && mode === null) throw new CountryFilterError('unexpected', 200);
  return { countries, mode: countries.length > 0 ? mode : null };
};

const toError = (err: unknown): CountryFilterError => {
  if (err instanceof CountryFilterError) return err;
  const status = axios.isAxiosError(err) ? (err.response?.status ?? 0) : 0;
  return new CountryFilterError(getErrorCode(err) ?? '', status);
};

/** The brand's stored filter. */
export const getCountryFilter = async (casinoInstanceId: string, signal?: AbortSignal): Promise<CountryFilter> => {
  try {
    const response = await axios.get(route(), {
      headers: authHeaders(),
      params: { casino_instance_id: casinoInstanceId },
      signal,
    });
    return parseCountryFilter(response.data);
  } catch (err) {
    throw toError(err);
  }
};

/** Replaces the brand's filter; answers what is now stored. An empty list removes the filter. */
export const saveCountryFilter = async (
  casinoInstanceId: string,
  countries: readonly string[],
  mode: CountryFilterMode,
): Promise<CountryFilter> => {
  try {
    const response = await axios.post(
      route(),
      { casino_instance_id: casinoInstanceId, country_list: countries, filter_mode: mode },
      { headers: authHeaders() },
    );
    return parseCountryFilter(response.data);
  } catch (err) {
    throw toError(err);
  }
};
